import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const SavingsVisualizationSection = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(15);

  const generateSavingsData = (years) => {
    const data = [];
    const initialInvestment = 10000;
    const yearlySavings = 1000;
    const energyPriceIncrease = 1.03; // 3% per year

    for (let year = 0; year <= years; year++) {
      const adjustedSavings = yearlySavings * Math.pow(energyPriceIncrease, year);
      const cumulativeSavings = year === 0 ? -initialInvestment : data?.[year - 1]?.cumulative + adjustedSavings;
      
      data?.push({
        year: `Jaar ${year}`,
        savings: Math.round(adjustedSavings),
        cumulative: Math.round(cumulativeSavings),
        investment: year === 0 ? initialInvestment : 0
      });
    }
    return data;
  };

  const savingsData = generateSavingsData(selectedPeriod);
  const breakEvenYear = savingsData?.findIndex(d => d?.cumulative >= 0);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Visualisatie van uw besparing
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Zie hoe uw investering zich in de loop der jaren terugverdient
          </p>
        </motion.div>

        {/* Period Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center gap-4 mb-8"
        >
          {[10, 15, 20, 25]?.map((period) => (
            <motion.button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                selectedPeriod === period
                  ? 'bg-primary text-white shadow-elevation-2'
                  : 'bg-white text-muted-foreground hover:bg-gray-100 shadow-elevation-1'
              }`}
            >
              {period} jaar
            </motion.button>
          ))}
        </motion.div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Cumulative Savings Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-elevation-2"
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Cumulatieve besparing</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={savingsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="year" 
                  tick={{ fontSize: 12 }}
                  interval={Math.floor(selectedPeriod / 5)}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
            {breakEvenYear > 0 && (
              <div className="mt-4 flex items-center justify-center space-x-2 text-sm">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-muted-foreground">
                  Break-even punt: <span className="font-bold text-success">Jaar {breakEvenYear}</span>
                </span>
              </div>
            )}
          </motion.div>

          {/* Yearly Savings Chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-elevation-2"
          >
            <h3 className="text-xl font-bold text-foreground mb-6">Jaarlijkse besparing</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={savingsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="year" 
                  tick={{ fontSize: 12 }}
                  interval={Math.floor(selectedPeriod / 5)}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="savings" 
                  fill="#10b981" 
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 flex items-center justify-center space-x-2 text-sm">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span className="text-muted-foreground">
                Inclusief 3% energieprijsstijging per jaar
              </span>
            </div>
          </motion.div>
        </div>

        {/* Key Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6"
        >
          <div className="bg-white rounded-xl p-6 shadow-elevation-2">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Icon name="Euro" size={24} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground mb-2">Totale besparing na {selectedPeriod} jaar</p>
            <p className="text-3xl font-bold text-foreground">
              €{savingsData?.[selectedPeriod]?.cumulative?.toLocaleString()}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-elevation-2">
            <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mb-4">
              <Icon name="TrendingUp" size={24} className="text-success" />
            </div>
            <p className="text-sm text-muted-foreground mb-2">Gemiddelde jaarlijkse besparing</p>
            <p className="text-3xl font-bold text-foreground">
              €{Math.round(savingsData?.reduce((sum, d) => sum + d?.savings, 0) / selectedPeriod)?.toLocaleString()}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-elevation-2">
            <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <Icon name="Percent" size={24} className="text-green-600" />
            </div>
            <p className="text-sm text-muted-foreground mb-2">Return on Investment</p>
            <p className="text-3xl font-bold text-foreground">
              {Math.round((savingsData?.[selectedPeriod]?.cumulative / 10000) * 100)}%
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SavingsVisualizationSection;